import React, { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import BriefAutofillDocument from 'components/common/icons/BriefAutofillDocument'
import BriefAutofillSparkle from 'components/common/icons/BriefAutofillSparkle'
import BriefAutofillQuestion from 'components/common/icons/BriefAutofillQuestion'
import BriefAutofillSpinner from 'components/common/icons/BriefAutofillSpinner'
import BriefAutofillCheck from 'components/common/icons/BriefAutofillCheck'
import { BriefAutofillState, MAX_TREATMENT_PLAN_BYTES, RETRYABLE_FAILURE_REASONS } from './useBriefAutofill'

// Sizes and offsets from the Figma design (HI-UPDATES-2026, Update brief)
const TEXT_COLOR = '#001335'
const ACCENT_COLOR = '#4151E6'
const ERROR_COLOR = '#EA3525'
const ICON_LEFT = 24
const ICON_WIDTH = 49
const TEXT_LEFT = 92
const FEEDBACK_LINE_HEIGHT = 19
const FEEDBACK_BOTTOM = 14

const useStyles = makeStyles(() => ({
  // Wider than the design's 370 so the description takes two lines in Dazzed, keeping the header short
  container: {
    position: 'relative',
    width: 410
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 100,
    padding: ({ hasFeedback }) =>
      `12px 16px ${hasFeedback ? FEEDBACK_BOTTOM + FEEDBACK_LINE_HEIGHT + 6 : 12}px ${ICON_LEFT}px`,
    border: ({ isError }) => `1.5px dashed ${isError ? ERROR_COLOR : ACCENT_COLOR}`,
    borderRadius: 4,
    cursor: ({ isBusy }) => (isBusy ? 'default' : 'pointer'),
    background: ({ isDragging }) => (isDragging ? 'rgba(65, 81, 230, 0.06)' : 'transparent'),
    outlineOffset: 2
  },
  icon: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON_WIDTH,
    minHeight: 58,
    marginRight: TEXT_LEFT - ICON_LEFT - ICON_WIDTH
  },
  sparkle: { position: 'absolute', left: 35, top: 43 },
  question: { position: 'absolute', left: -9, top: 42 },
  // Each bar dims in turn, so the bright one travels clockwise
  spinnerBar: {
    animation: '$fade 1s linear infinite',
    animationDelay: 'calc(var(--bar-index) * 0.125s - 1s)',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
  },
  '@keyframes fade': {
    from: { opacity: 1 },
    to: { opacity: 0.25 }
  },
  title: {
    fontWeight: '600 !important',
    lineHeight: `${FEEDBACK_LINE_HEIGHT}px`
  },
  description: {
    color: TEXT_COLOR,
    lineHeight: `${FEEDBACK_LINE_HEIGHT}px`
  },
  // The modal renders its title, and this zone with it, inside the serif heading: set the font here
  uploadLine: {
    marginTop: 8,
    fontFamily: 'Dazzed',
    fontSize: 13,
    color: TEXT_COLOR,
    lineHeight: `${FEEDBACK_LINE_HEIGHT}px`
  },
  link: {
    textDecoration: 'underline',
    color: ACCENT_COLOR,
    fontWeight: 600
  },
  feedback: {
    position: 'absolute',
    left: TEXT_LEFT,
    bottom: FEEDBACK_BOTTOM,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'Dazzed',
    fontSize: 13,
    lineHeight: `${FEEDBACK_LINE_HEIGHT}px`
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0
  }
}))

const STATUS_REGION_ID = 'patient-brief-ai-autofill-status'

// react-dropzone rejection codes -> the reasons the modal tracks and the zone explains
const REJECTION_REASONS = {
  'file-invalid-type': 'not_pdf',
  'file-too-large': 'too_large',
  'too-many-files': 'too_many_files'
}

const CLIENT_REJECTION_MESSAGES = {
  not_pdf: 'dialogs.patientBrief.autofill.rejectedNotPdf',
  too_large: 'dialogs.patientBrief.autofill.rejectedTooLarge',
  too_many_files: 'dialogs.patientBrief.autofill.rejectedTooManyFiles'
}

const failureMessageKey = failureReason =>
  CLIENT_REJECTION_MESSAGES[failureReason] ||
  (RETRYABLE_FAILURE_REASONS.includes(failureReason)
    ? 'dialogs.patientBrief.autofill.failedTryAgainDescription'
    : 'dialogs.patientBrief.autofill.failedDescription')

// An analysis ran and finished: worth a word from the reviewer (not for files rejected before upload)
const FEEDBACK_STATES = [
  BriefAutofillState.Done,
  BriefAutofillState.NothingFilled,
  BriefAutofillState.NotAPlan,
  BriefAutofillState.Failed
]

const BriefAutofillDropzone = ({ state, failureReason, onFileSelected, onFileRejected, onSubmitFeedback }) => {
  const { t } = useTranslation()
  const isBusy = state === BriefAutofillState.Analyzing

  // While busy, files are ignored here rather than through `disabled`: a disabled zone stops calling
  // preventDefault, and a PDF dropped onto it would open in the tab and discard the unsaved brief.
  const handleDropAccepted = useCallback(files => !isBusy && onFileSelected(files[0]), [isBusy, onFileSelected])
  const handleDropRejected = useCallback(
    rejections => {
      if (isBusy) {
        return
      }
      const code = rejections[0]?.errors?.[0]?.code
      onFileRejected(REJECTION_REASONS[code] || 'not_pdf')
    },
    [isBusy, onFileRejected]
  )

  // MIME type only (no '.pdf' extension): a .pdf name with a conflicting type is rejected before upload.
  // preventDropOnDocument (on by default) stops a drop just outside the zone from opening the PDF in the tab.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'application/pdf',
    maxSize: MAX_TREATMENT_PLAN_BYTES,
    multiple: false,
    noClick: isBusy,
    noKeyboard: isBusy,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected
  })

  const isError = state === BriefAutofillState.Failed || state === BriefAutofillState.NotAPlan
  const hasFeedback = FEEDBACK_STATES.includes(state) && !CLIENT_REJECTION_MESSAGES[failureReason]
  const classes = useStyles({ isError, isBusy, isDragging: isDragActive, hasFeedback })

  const documentIcon = badge => (
    <>
      <BriefAutofillDocument />
      {badge}
    </>
  )

  const content = {
    [BriefAutofillState.Idle]: {
      icon: documentIcon(<BriefAutofillSparkle className={classes.sparkle} />),
      title: t('dialogs.patientBrief.autofill.title'),
      description: t('dialogs.patientBrief.autofill.description'),
      uploadLine: (
        <>
          {t('dialogs.patientBrief.autofill.dragAndDrop')}{' '}
          <span className={classes.link}>{t('dialogs.patientBrief.autofill.upload')}</span>
        </>
      )
    },
    [BriefAutofillState.Analyzing]: {
      icon: <BriefAutofillSpinner barClassName={classes.spinnerBar} />,
      title: t('dialogs.patientBrief.autofill.analyzingTitle'),
      description: t('dialogs.patientBrief.autofill.analyzingDescription')
    },
    [BriefAutofillState.Done]: {
      icon: <BriefAutofillCheck />,
      title: t('dialogs.patientBrief.autofill.doneTitle'),
      description: t('dialogs.patientBrief.autofill.doneDescription')
    },
    [BriefAutofillState.NothingFilled]: {
      icon: documentIcon(),
      title: t('dialogs.patientBrief.autofill.nothingFilledTitle'),
      description: t('dialogs.patientBrief.autofill.nothingFilledDescription')
    },
    [BriefAutofillState.Failed]: {
      icon: documentIcon(<BriefAutofillQuestion className={classes.question} />),
      title: t('dialogs.patientBrief.autofill.failedTitle'),
      description: t(failureMessageKey(failureReason))
    },
    [BriefAutofillState.NotAPlan]: {
      icon: documentIcon(<BriefAutofillQuestion className={classes.question} />),
      title: t('dialogs.patientBrief.autofill.failedTitle'),
      description: t('dialogs.patientBrief.autofill.notAPlanDescription')
    }
  }[state]

  const Description = state === BriefAutofillState.Idle ? DazzedParagraph12 : DazzedParagraph14

  return (
    <div className={classes.container}>
      <div
        className={classes.root}
        {...getRootProps({
          id: 'patient-brief-ai-autofill-dropzone',
          role: 'button',
          'aria-label': t('dialogs.patientBrief.autofill.title'),
          'aria-disabled': isBusy,
          'aria-describedby': STATUS_REGION_ID
        })}
      >
        <input {...getInputProps()} />
        <div className={classes.icon} aria-hidden>
          {content.icon}
        </div>
        <div>
          <DazzedParagraph14 color={TEXT_COLOR} className={classes.title}>
            {content.title}
          </DazzedParagraph14>
          <Description color={TEXT_COLOR} className={classes.description}>
            {content.description}
          </Description>
          {content.uploadLine && <div className={classes.uploadLine}>{content.uploadLine}</div>}
        </div>
      </div>
      {/* Outside the drop zone: a control nested in role="button" is unreachable, and its click would open the picker */}
      {hasFeedback && (
        <button
          type="button"
          id="patient-brief-ai-autofill-feedback"
          className={[classes.feedback, classes.link].join(' ')}
          onClick={onSubmitFeedback}
        >
          {t('dialogs.patientBrief.autofill.submitFeedback')}
        </button>
      )}
      {/* Outside the button: role="button" hides its children from assistive technology */}
      <span id={STATUS_REGION_ID} aria-live="polite" className={classes.visuallyHidden}>
        {content.title}. {content.description}
      </span>
    </div>
  )
}

export default BriefAutofillDropzone
